import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, ChevronUp, Search, X } from 'react-feather';

function InlineMultiSelect({
  value = [],
  options = [],
  onChange,
  placeholder = 'Select...',
  rowActive = false,
  disabled = false,
  minWidth = 320,
  ariaLabel,
  // See InlineSelect — callers inside a high-z modal must raise the portal menu.
  menuZIndex = 9999,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const trigRef = useRef(null);
  const srchRef = useRef(null);
  const menuRef = useRef(null);
  const [mp, setMp] = useState({});

  const normalizedValue = useMemo(() => {
    return (Array.isArray(value) ? value : [])
      .map((item) => (item && typeof item === 'object' ? item.value : item))
      .filter((item) => item !== undefined && item !== null);
  }, [value]);

  const selectedSet = useMemo(() => new Set(normalizedValue), [normalizedValue]);
  const selected = useMemo(() => {
    const byValue = new Map(options.map((option) => [option.value, option]));
    return normalizedValue.map((item) => {
      const found = byValue.get(item);
      if (found) return found;
      const fallback = (Array.isArray(value) ? value : []).find((entry) => entry && typeof entry === 'object' && entry.value === item);
      return fallback || { value: item, label: String(item) };
    });
  }, [normalizedValue, options, value]);

  const opts = useMemo(
    () => (!q ? options : options.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase()))),
    [options, q]
  );

  const openMenu = useCallback(() => {
    if (!trigRef.current || disabled) return;
    const r = trigRef.current.getBoundingClientRect();
    const w = Math.max(r.width, minWidth);
    const maxMenuHeight = 392;
    const estH = Math.min(opts.length * 44 + 114 + (selected.length ? 46 : 0), maxMenuHeight);
    const spaceBelow = window.innerHeight - r.bottom - 10;
    let left = r.left;
    if (left + w > window.innerWidth - 10) left = window.innerWidth - w - 10;
    setMp({
      top: spaceBelow >= estH ? r.bottom + 8 : Math.max(10, r.top - estH - 8),
      left,
      width: w,
      maxHeight: maxMenuHeight,
    });
    setOpen(true);
    setHi(0);
    setTimeout(() => srchRef.current?.focus(), 10);
  }, [disabled, minWidth, opts.length, selected.length]);

  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);

  const emit = useCallback((nextValues) => {
    const byValue = new Map(options.map((option) => [option.value, option]));
    onChange(nextValues.map((item) => byValue.get(item) || { value: item, label: String(item) }));
  }, [onChange, options]);

  const toggle = useCallback((option) => {
    const nextSet = new Set(selectedSet);
    if (nextSet.has(option.value)) nextSet.delete(option.value);
    else nextSet.add(option.value);
    emit(Array.from(nextSet));
  }, [emit, selectedSet]);

  const clear = useCallback(() => {
    emit([]);
    setQ('');
    setHi(0);
  }, [emit]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !trigRef.current?.contains(e.target)) close();
    };
    const onResize = () => close();
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [close, open]);

  const tKey = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const mKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((i) => Math.min(i + 1, opts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (opts[hi]) toggle(opts[hi]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const show = (rowActive || open) && !disabled;
  const visibleSelected = selected.slice(0, 1);
  const overflowCount = Math.max(0, selected.length - visibleSelected.length);

  return (
    <div style={{ width: '100%', padding: '0 4px' }}>
      <button
        ref={trigRef}
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={open ? close : openMenu}
        onKeyDown={tKey}
        title={selected.map((item) => item.label).join(', ')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          minHeight: 34,
          boxSizing: 'border-box',
          padding: '4px 8px',
          borderRadius: 8,
          outline: 'none',
          textAlign: 'left',
          minWidth: 0,
          cursor: disabled ? 'default' : 'pointer',
          border: `1px solid ${open ? 'var(--lodge-orange-400)' : show ? 'var(--lodge-onyx-200)' : 'transparent'}`,
          background: open ? 'var(--lodge-orange-50)' : 'var(--lodge-white)',
          boxShadow: open ? 'var(--focus)' : 'none',
          transition: 'border-color .12s, background .12s, box-shadow .12s',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          {selected.length === 0 ? (
            <span style={{ fontSize: 13, color: show ? 'var(--lodge-onyx-400)' : 'var(--lodge-onyx-300)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {placeholder}
            </span>
          ) : (
            <>
              {visibleSelected.map((item) => (
                <span
                  key={item.value}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    padding: '2px 7px',
                    borderRadius: 6,
                    background: 'var(--lodge-onyx-50)',
                    border: '1px solid var(--lodge-onyx-100)',
                    color: 'var(--lodge-onyx-700)',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </span>
              ))}
              {overflowCount > 0 && (
                <span style={{ flexShrink: 0, color: 'var(--lodge-onyx-500)', fontSize: 12, fontWeight: 600 }}>
                  +{overflowCount}
                </span>
              )}
            </>
          )}
        </span>
        {selected.length > 0 && (
          <span
            role="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); clear(); }}
            style={{
              padding: 3,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              color: 'var(--lodge-onyx-400)',
              opacity: show && !open ? 1 : 0,
              pointerEvents: show && !open ? 'auto' : 'none',
              cursor: 'pointer',
              transition: 'opacity .12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lodge-onyx-100)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={12} />
          </span>
        )}
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: show ? 1 : 0, transition: 'opacity .12s' }}>
          {open
            ? <ChevronUp size={14} color="var(--lodge-orange-400)" />
            : <ChevronDown size={14} color="var(--lodge-onyx-400)" />}
        </span>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            ...mp,
            zIndex: menuZIndex,
            background: 'var(--lodge-white)',
            border: '1px solid var(--lodge-onyx-100)',
            borderRadius: 14,
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
            overflow: 'hidden',
            animation: 'it-menuIn .12s ease-out',
          }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--lodge-onyx-100)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--lodge-onyx-50)', borderRadius: 10, padding: '10px 12px' }}>
              <Search size={16} color="var(--lodge-onyx-400)" />
              <input
                ref={srchRef}
                type="text"
                value={q}
                placeholder="Search..."
                onChange={(e) => { setQ(e.target.value); setHi(0); }}
                onKeyDown={mKey}
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 14, flex: 1, minWidth: 0, color: 'var(--lodge-onyx-900)', fontFamily: 'var(--font-sans)' }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={clear}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '11px 16px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  background: 'transparent',
                  color: 'var(--lodge-onyx-400)',
                  textAlign: 'left',
                  borderBottom: '1px solid var(--lodge-onyx-50)',
                  fontFamily: 'var(--font-sans)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lodge-onyx-50)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <X size={14} color="var(--lodge-onyx-300)" /> Clear
              </button>
            )}
            {opts.length === 0 ? (
              <div style={{ padding: '18px 16px', textAlign: 'center', fontSize: 14, color: 'var(--lodge-onyx-400)' }}>No results</div>
            ) : (
              opts.map((o, i) => {
                const isSelected = selectedSet.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggle(o)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'left',
                      fontFamily: 'var(--font-sans)',
                      background: isSelected ? 'var(--lodge-orange-50)' : i === hi ? 'var(--lodge-onyx-50)' : 'transparent',
                      color: isSelected ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-900)',
                      fontWeight: isSelected ? 700 : 500,
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--lodge-onyx-50)'; setHi(i); }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = i === hi ? 'var(--lodge-onyx-50)' : 'transparent'; }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                    {isSelected && <Check size={15} color="var(--lodge-orange-400)" style={{ flexShrink: 0 }} />}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default InlineMultiSelect;
